import type { Prediction } from './calibration'
import { brierScore } from './calibration'
import { clamp } from './statistics'

export interface DriftPoint {
  index: number
  brier: number
}

export function rollingBrier(predictions: readonly Prediction[], window = 10): DriftPoint[] {
  const out: DriftPoint[] = []
  if (predictions.length < window) return out
  for (let i = window - 1; i < predictions.length; i++) {
    out.push({ index: i, brier: brierScore(predictions.slice(i - window + 1, i + 1)) })
  }
  return out
}

export function accuracy(predictions: readonly Prediction[]): number {
  if (predictions.length === 0) return 0
  let hit = 0
  for (const p of predictions) if ((p.prob >= 0.5 ? 1 : 0) === p.outcome) hit++
  return hit / predictions.length
}

// Population Stability Index between two probability distributions over fixed [0,1] bins.
export function psi(baseline: readonly number[], recent: readonly number[], buckets = 10): number {
  if (baseline.length === 0 || recent.length === 0) return 0
  const eps = 1e-4
  let total = 0
  for (let b = 0; b < buckets; b++) {
    const lo = b / buckets
    const hi = (b + 1) / buckets
    const inBin = (v: number) => v >= lo && (b === buckets - 1 ? v <= hi : v < hi)
    const basePct = Math.max(eps, baseline.filter(inBin).length / baseline.length)
    const recentPct = Math.max(eps, recent.filter(inBin).length / recent.length)
    total += (recentPct - basePct) * Math.log(recentPct / basePct)
  }
  return total
}

export type PsiLevel = 'stable' | 'moderate' | 'significant'

export function psiLevel(value: number): PsiLevel {
  if (value < 0.1) return 'stable'
  if (value < 0.25) return 'moderate'
  return 'significant'
}

export type DriftVerdict = 'insufficient' | 'stable' | 'drifting' | 'broken'

export interface DriftReport {
  count: number
  baselineBrier: number
  recentBrier: number
  brierDelta: number
  baselineAccuracy: number
  recentAccuracy: number
  accuracyDelta: number
  psi: number
  psiLevel: PsiLevel
  verdict: DriftVerdict
  reasons: string[]
  rolling: DriftPoint[]
}

export function detectDrift(
  predictions: readonly Prediction[],
  opts: { baselineFraction?: number; minSamples?: number; window?: number } = {},
): DriftReport {
  const minSamples = opts.minSamples ?? 20
  const window = opts.window ?? 10
  const count = predictions.length

  if (count < minSamples) {
    return {
      count,
      baselineBrier: 0,
      recentBrier: 0,
      brierDelta: 0,
      baselineAccuracy: 0,
      recentAccuracy: 0,
      accuracyDelta: 0,
      psi: 0,
      psiLevel: 'stable',
      verdict: 'insufficient',
      reasons: [`Only ${count} resolved predictions — need at least ${minSamples} to judge drift.`],
      rolling: rollingBrier(predictions, window),
    }
  }

  const split = Math.floor(count * (opts.baselineFraction ?? 0.5))
  const baseline = predictions.slice(0, split)
  const recent = predictions.slice(split)

  const baselineBrier = brierScore(baseline)
  const recentBrier = brierScore(recent)
  const brierDelta = recentBrier - baselineBrier
  const baselineAccuracy = accuracy(baseline)
  const recentAccuracy = accuracy(recent)
  const accuracyDelta = recentAccuracy - baselineAccuracy
  const psiValue = psi(baseline.map((p) => p.prob), recent.map((p) => p.prob))
  const level = psiLevel(psiValue)

  const reasons: string[] = []
  if (brierDelta > 0.04) reasons.push(`Brier score worsened by ${brierDelta.toFixed(3)} in the recent half — predictions are getting less accurate.`)
  if (accuracyDelta < -0.1) reasons.push(`Hit rate dropped ${Math.abs(accuracyDelta * 100).toFixed(0)} points recently.`)
  if (level !== 'stable') reasons.push(`Predicted-probability distribution shifted (PSI ${psiValue.toFixed(2)}, ${level}).`)

  let verdict: DriftVerdict = 'stable'
  if (brierDelta > 0.08 && level === 'significant') verdict = 'broken'
  else if (reasons.length > 0) verdict = 'drifting'
  if (verdict === 'stable') reasons.push('No meaningful drift detected. Recent accuracy and distribution match the baseline.')

  return {
    count,
    baselineBrier,
    recentBrier,
    brierDelta,
    baselineAccuracy,
    recentAccuracy,
    accuracyDelta,
    psi: psiValue,
    psiLevel: level,
    verdict,
    reasons,
    rolling: rollingBrier(predictions, window),
  }
}

export function driftScore(report: DriftReport): number {
  if (report.verdict === 'insufficient') return 0
  return clamp(report.psi * 120 + Math.max(0, report.brierDelta) * 300 + Math.max(0, -report.accuracyDelta) * 150, 0, 100)
}
